import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Scale, KeyRound, Menu, X, ArrowRight } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const LINKS = [
  { label: "How it works", href: "#how-it-works", testid: "nav-link-how" },
  { label: "For families", href: "#families", testid: "nav-link-families" },
  { label: "For business", href: "#business", testid: "nav-link-business" },
  { label: "Plans", href: "#plans", testid: "nav-link-plans" },
  { label: "Partner up", href: "#partner", testid: "nav-link-partner" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/70"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-brand"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 rounded-lg bg-[#1D4ED8] text-white flex items-center justify-center transition-transform duration-300 group-hover:rotate-6">
            <Scale size={18} strokeWidth={2.2} />
          </span>
          <span className="text-left leading-none">
            <span className="font-display font-extrabold text-lg tracking-tight text-[#0B192C] block">John Jones</span>
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-blue-600 block mt-1">Independent Associate</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-slate-600 hover:text-blue-700 transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            data-testid="nav-admin-link"
            to="/admin"
            className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-blue-700 hover:border-blue-300 transition-colors duration-200"
            title="Associate login"
          >
            <KeyRound size={16} />
          </Link>
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#get-started")}
            className="group flex items-center gap-2 bg-[#1D4ED8] hover:bg-blue-800 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            Get the details
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden w-10 h-10 flex items-center justify-center text-slate-700"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-slate-200/70 bg-white/95 backdrop-blur-md"
            data-testid="nav-mobile-panel"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.href)}
                  className="text-left text-base font-medium text-slate-700"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-cta-button-mobile"
                onClick={() => go("#get-started")}
                className="bg-[#1D4ED8] text-white text-sm font-semibold px-5 py-3 rounded-full w-max"
              >
                Get the details
              </button>
              <Link data-testid="nav-admin-link-mobile" to="/admin" className="text-sm text-slate-500 flex items-center gap-2">
                <KeyRound size={14} /> Associate login
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
